import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import type { BreakingStageType } from '../constants/breaking';
import type { BracketSide } from '../helpers/bracketLayout';

/**
 * Breaking bracket pushed by the desktop.
 *
 * Mirrors `BreakingBracketView` in
 * app/src/helpers/breakingBracketView.ts - keep both shapes in sync. Everything
 * is pre-resolved on the desktop (display names, per-side battle-round tallies,
 * stage labels), so these screens only paint what they are given.
 *
 * It arrives as a const socket keyed per category, which is what makes a monitor
 * that reconnects mid-battle come back with the bracket still on it. Not
 * persisted: a stale bracket from a previous session must never come back up.
 */

export interface BracketViewSide {
  name: string;
  country?: string;
  customFlag?: string;
  /** Battle rounds this side has won so far. */
  roundsWon: number;
  isWinner: boolean;
  /** Shown while the slot is empty ("Winner of Battle 3", "Bye"). */
  placeholder?: string;
}

export interface BracketViewBattle {
  uuid: string;
  stage: BreakingStageType | string;
  /** Displayed as "Battle {slot + 1}". */
  slot: number;
  red: BracketViewSide;
  blue: BracketViewSide;
  danced: number;
  total: number;
  finished: boolean;
  /** Which half of the draw the battle belongs to. */
  side: BracketSide;
  /** Distance from the final: 0 = final, 1 = semi-final, … */
  depth: number;
}

export interface BracketViewStage {
  stage: BreakingStageType | string;
  label: string;
  battles: BracketViewBattle[];
}

export interface BreakingBracketView {
  categoryUuid: string;
  categoryName: string;
  ttUuid?: string;
  /** The whole ladder in running order; a pane shows only `focusStages` of it. */
  stages: BracketViewStage[];
  /** The slice a details pane shows: the stage being danced and the one it feeds. */
  focusStages: (BreakingStageType | string)[];
  bronze?: BracketViewBattle;
  /** Ready to display, like a stage's `label`. */
  bronzeLabel?: string;
  /** Battle on the floor and still undecided - marked "Now!". */
  currentBattleUuid?: string;
  /** The operator asked for the whole ladder, shown fullscreen. */
  full: boolean;
  /** False means take it down. */
  visible: boolean;
  updatedAt: number;
}

interface BreakingBracketState {
  byCategory: Record<string, BreakingBracketView>;
}

const initialState: BreakingBracketState = {
  byCategory: {},
};

const breakingBracketSlice = createSlice({
  name: 'breakingBracket',
  initialState,
  reducers: {
    setBreakingBracket(state, action: PayloadAction<BreakingBracketView>) {
      const view = action.payload;
      if (!view?.categoryUuid) return;
      if (view.visible === false) {
        delete state.byCategory[view.categoryUuid];
        return;
      }
      // Const sockets are replayed on reconnect, so an older frame can arrive
      // after a newer one; newest wins.
      const existing = state.byCategory[view.categoryUuid];
      if (existing && (existing.updatedAt ?? 0) > (view.updatedAt ?? 0)) return;
      state.byCategory[view.categoryUuid] = view;
    },
    clearBreakingBrackets(state) {
      state.byCategory = {};
    },
  },
});

export const { setBreakingBracket, clearBreakingBrackets } =
  breakingBracketSlice.actions;

export const selectBracketByCategory = (
  state: RootState,
  categoryUuid?: string,
): BreakingBracketView | undefined =>
  categoryUuid ? state.breakingBracket?.byCategory?.[categoryUuid] : undefined;

/** The bracket pushed for one timetable item, whichever category it belongs to. */
export const selectBracketForTtItem = (
  state: RootState,
  ttUuid?: string,
): BreakingBracketView | undefined => {
  if (!ttUuid) return undefined;
  return Object.values(state.breakingBracket?.byCategory ?? {}).find(
    (view) => view.ttUuid === ttUuid,
  );
};

/**
 * The bracket the operator put up fullscreen, if any. Only one can be showing:
 * the monitor is a single screen and the takeover covers it.
 */
export const selectFullscreenBracket = (
  state: RootState,
): BreakingBracketView | undefined =>
  Object.values(state.breakingBracket?.byCategory ?? {}).find(
    (view) => view.full,
  );

export default breakingBracketSlice.reducer;
